import type { PageField } from './pageContract';

export function validatePageFilters(changes: Record<string, unknown>, schema: Record<string, PageField>) {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) throw new Error('Filters must be an object.');
  for (const [key, value] of Object.entries(changes)) {
    const field = Object.prototype.hasOwnProperty.call(schema, key) ? schema[key] : undefined;
    if (!field) throw new Error(`Unsupported filter: ${key}`);
    if (field.type === 'enum' && field.multiple && typeof value === 'string') {
      const members = value.split(',').map((item) => item.trim());
      const ambiguous = field.values?.some((option) => {
        if (typeof option !== 'string' || !option.includes(',')) return false;
        const parts = option.split(',').map((item) => item.trim());
        return members.some((_, start) => parts.every((part, offset) => members[start + offset] === part));
      });
      if (ambiguous) throw new Error(`${key}: comma-containing values cannot be selected by chat.`);
    }
    if (
      field.type === 'enum' &&
      (field.multiple
        ? (Array.isArray(value)
            ? value
            : typeof value === 'string'
              ? value.split(',').map((item) => item.trim())
              : [value]
          ).some((item) => !field.values?.includes(item))
        : !field.values?.includes(value as string))
    )
      throw new Error(`Invalid ${key}: use one of its declared values.`);
    if (
      field.multiple &&
      (typeof value === 'string' || Array.isArray(value)) &&
      field.maxSelections &&
      new Set(
        (Array.isArray(value) ? value : value.split(','))
          .map((item) => (typeof item === 'string' ? item.trim() : item))
          .filter(Boolean),
      ).size > field.maxSelections
    )
      throw new Error(`${key} allows at most ${field.maxSelections} selections.`);
    if (field.type === 'text' && (typeof value !== 'string' || value.length > (field.maxLength ?? 200)))
      throw new Error(`${key} must be text of at most ${field.maxLength ?? 200} characters.`);
    if (field.type === 'boolean' && typeof value !== 'boolean') throw new Error(`${key} must be true or false.`);
    if (
      field.type === 'date' &&
      (typeof value !== 'string' ||
        !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        Number.isNaN(Date.parse(value)) ||
        new Date(value).toISOString().slice(0, 10) !== value)
    )
      throw new Error(`${key} must be a valid YYYY-MM-DD date.`);
  }
}

/** A multi-select edit sent as a list becomes the contract's comma-separated value. */
export function joinPageFilterLists(changes: Record<string, unknown>, schema: Record<string, PageField>) {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) return changes;
  return Object.fromEntries(
    Object.entries(changes).map(([key, value]) => [
      key,
      schema[key]?.multiple &&
      Array.isArray(value) &&
      value.every((item) => typeof item === 'string' && !item.includes(','))
        ? value.join(',')
        : value,
    ]),
  );
}

/** Multi-select edits settle by membership, independent of order or duplicate input. */
export function pageFilterValuesEqual(actual: unknown, requested: unknown, field?: PageField): boolean {
  if (
    field?.multiple &&
    (Array.isArray(actual) || typeof actual === 'string') &&
    (Array.isArray(requested) || typeof requested === 'string')
  ) {
    const canonical = (value: string | unknown[]) =>
      [
        ...new Set(
          (Array.isArray(value) ? value : value.split(','))
            .map((item) => (typeof item === 'string' ? item.trim() : item))
            .filter(Boolean),
        ),
      ]
        .sort()
        .join(',');
    return canonical(actual) === canonical(requested);
  }
  return actual === requested;
}
